import { useEffect, useRef, useState } from 'react';
import {
  applyPromptValues,
  extractPromptMarkers,
  filledPromptMarkerCount,
  normalizedPromptValue,
  reconcilePromptValues,
  type MarkerValues,
  type PromptMarker
} from './promptTemplate';

export interface ActivePromptMarker {
  marker: string;
  occurrence: number;
}

export interface PreparedPromptCopy {
  text: string;
  placeholderTotal: number;
  placeholderFilled: number;
}

interface DraftState {
  identity: string;
  values: Record<string, string>;
  copiedValues: Record<string, string>;
  preparedValues: Record<string, string> | null;
  active: ActivePromptMarker | null;
  draft: string;
}

export interface PromptDraftController {
  markers: PromptMarker[];
  values: MarkerValues;
  active: ActivePromptMarker | null;
  draft: string;
  text: string;
  filledCount: number;
  pending: boolean;
  begin(marker: string, occurrence: number): void;
  updateDraft(value: string): void;
  confirm(): void;
  cancel(): void;
  clear(): void;
  prepareCopy(): PreparedPromptCopy;
  markCopied(): void;
}

function initial(identity: string): DraftState {
  return {
    identity,
    values: {},
    copiedValues: {},
    preparedValues: null,
    active: null,
    draft: ''
  };
}

function committed(state: DraftState): DraftState {
  if (!state.active) return state;
  const value = normalizedPromptValue(state.draft);
  const values = { ...state.values };
  if (value === '') delete values[state.active.marker];
  else values[state.active.marker] = value;
  return { ...state, values, active: null, draft: '' };
}

function comparableValues(state: DraftState): Record<string, string> {
  return committed(state).values;
}

function equalValues(left: MarkerValues, right: MarkerValues): boolean {
  const leftEntries = Object.entries(left);
  const rightEntries = Object.entries(right);
  return leftEntries.length === rightEntries.length &&
    leftEntries.every(([key, value]) => right[key] === value);
}

export function usePromptDraft({ identity, template }: { identity: string; template: string }): PromptDraftController {
  const [rendered, setRendered] = useState<DraftState>(() => initial(identity));
  const state = useRef(rendered);
  const markers = extractPromptMarkers(template);

  function replace(update: (current: DraftState) => DraftState): DraftState {
    const next = update(state.current);
    state.current = next;
    setRendered(next);
    return next;
  }

  useEffect(() => {
    replace((current) => {
      if (current.identity !== identity) return initial(identity);
      const values = reconcilePromptValues(template, current.values);
      const copiedValues = reconcilePromptValues(template, current.copiedValues);
      const preparedValues = current.preparedValues
        ? reconcilePromptValues(template, current.preparedValues)
        : null;
      const active = current.active && markers.some((marker) => marker.name === current.active?.marker)
        ? current.active
        : null;
      return {
        ...current,
        values,
        copiedValues,
        preparedValues,
        active,
        draft: active ? current.draft : ''
      };
    });
  }, [identity, template]);

  const previewValues = comparableValues(rendered);

  return {
    markers,
    values: rendered.values,
    active: rendered.active,
    draft: rendered.draft,
    text: applyPromptValues(template, rendered.values),
    filledCount: filledPromptMarkerCount(markers, rendered.values),
    pending:
      !equalValues(previewValues, rendered.copiedValues) ||
      (rendered.active !== null &&
        rendered.draft !== '' &&
        normalizedPromptValue(rendered.draft) === '' &&
        normalizedPromptValue(rendered.values[rendered.active.marker]) === ''),
    begin(marker, occurrence) {
      replace((current) => {
        const next = committed(current);
        return {
          ...next,
          active: { marker, occurrence },
          draft: next.values[marker] ?? ''
        };
      });
    },
    updateDraft(value) {
      replace((current) => ({ ...current, draft: value }));
    },
    confirm() {
      replace(committed);
    },
    cancel() {
      replace((current) => ({ ...current, active: null, draft: '' }));
    },
    clear() {
      replace((current) => ({
        ...current,
        values: {},
        copiedValues: {},
        preparedValues: null,
        active: null,
        draft: ''
      }));
    },
    prepareCopy() {
      const next = replace((current) => {
        const prepared = committed(current);
        return { ...prepared, preparedValues: { ...prepared.values } };
      });
      return {
        text: applyPromptValues(template, next.values),
        placeholderTotal: markers.length,
        placeholderFilled: filledPromptMarkerCount(markers, next.values)
      };
    },
    markCopied() {
      replace((current) =>
        current.preparedValues
          ? {
              ...current,
              copiedValues: { ...current.preparedValues },
              preparedValues: null
            }
          : current
      );
    }
  };
}
